import { useCallback, useEffect, useRef, useState } from "react";

export default function useResize(props: {
    onResize?: (...args: any[]) => any;
    initialSize?: {
        width?: number;
        height?: number;
    };
    boundries?: {
        MIN_COLUMN_WIDTH?: number;
        MAX_COLUMN_WIDTH?: number;
        MIN_HEIGHT?: number;
        MAX_HEIGHT?: number;
    };
}) {
    const isResizingRef = useRef(false);
    const elementRef = useRef<HTMLElement>(null);
    const [width, setWidth] = useState(props.initialSize?.width ?? 0);
    const [height, setHeight] = useState(props.initialSize?.height ?? 0);

    const clampWidth = useCallback(
        (value: number) =>
            Math.min(
                Math.max(value, props.boundries?.MIN_COLUMN_WIDTH ?? 0),
                props.boundries?.MAX_COLUMN_WIDTH ?? 500
            ),
        [props.boundries?.MIN_COLUMN_WIDTH, props.boundries?.MAX_COLUMN_WIDTH]
    );

    const clampHeight = useCallback(
        (value: number) =>
            Math.min(
                Math.max(value, props.boundries?.MIN_HEIGHT ?? 0),
                props.boundries?.MAX_HEIGHT ?? 500
            ),
        [props.boundries?.MIN_HEIGHT, props.boundries?.MAX_HEIGHT]
    );

    const handleMouseMove = (event: MouseEvent) => {
        if (!isResizingRef.current) return;

        const rect = elementRef.current.getBoundingClientRect();

        const newWidth = clampWidth(rect.width - event.movementX);
        const newHeight = clampHeight(rect.height + event.movementY);
        setWidth(newWidth);
        setHeight(newHeight);
    };

    const handleMouseUp = () => {
        if (!isResizingRef.current) return;

        if (props.onResize) {
            props.onResize();
        }

        isResizingRef.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
    };

    useEffect(() => {
        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isResizingRef.current, elementRef.current]);

    const handleResizeStart = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!elementRef.current) {
            console.error("Element reference is not set");
            return;
        }

        isResizingRef.current = true;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
    };

    const handleSetReference = (element: HTMLElement) => {
        elementRef.current = element;
    };

    return {
        size: { width, height },
        resizeStart: handleResizeStart,
        setReference: handleSetReference,
    };
}
